import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ForgotPasswordStateData {
  // Step 1 data
  email?: string;

  // Step 2 data (for 2FA flow)
  password?: string;
  confirmPassword?: string;

  // Flow state
  currentStep?: 'SEND' | 'CONFIRM';

  // Form interaction states
  formTouched?: {
    email?: boolean;
    password?: boolean;
    confirmPassword?: boolean;
  };

  // Metadata
  expiresAt?: number; // Timestamp for auto-cleanup
  createdAt?: number; // When the flow was started
}

@Injectable({
  providedIn: 'root',
})
export class MzicForgotPasswordStateService {
  private readonly FORGOT_PASSWORD_STATE_KEY =
    'mzic_forgot_password_flow_state';
  private readonly DEFAULT_EXPIRATION_MINUTES = 30; // 30 minutes default expiration
  private readonly serviceId = Math.random().toString(36).substr(2, 9); // Unique ID for debugging

  private forgotPasswordStateSource =
    new BehaviorSubject<ForgotPasswordStateData>({});
  public forgotPasswordState$ = this.forgotPasswordStateSource.asObservable();

  constructor() {
    this.initStateFromStorage();
  }

  private initStateFromStorage(): void {
    try {
      const savedState = localStorage.getItem(this.FORGOT_PASSWORD_STATE_KEY);

      if (savedState) {
        const parsedState = JSON.parse(savedState);

        // Check if state has expired
        if (parsedState.expiresAt && Date.now() > parsedState.expiresAt) {
          this.clearState();
          return;
        }

        this.forgotPasswordStateSource.next(parsedState);
      }
    } catch (error) {
      this.clearState();
    }
  }

  /**
   * Save email from step 1
   */
  saveEmailData(email: string): void {
    const currentState = this.getForgotPasswordState();
    const updatedState: ForgotPasswordStateData = {
      ...currentState,
      email,
      currentStep: 'SEND',
      createdAt: currentState.createdAt || Date.now(),
      expiresAt: Date.now() + this.DEFAULT_EXPIRATION_MINUTES * 60 * 1000,
    };

    this.saveState(updatedState);
  }

  /**
   * Save password data for 2FA flow
   */
  savePasswordData(password: string, confirmPassword: string): void {
    const currentState = this.getForgotPasswordState();
    const updatedState: ForgotPasswordStateData = {
      ...currentState,
      password,
      confirmPassword,
      currentStep: 'CONFIRM',
      createdAt: currentState.createdAt || Date.now(),
      expiresAt: Date.now() + this.DEFAULT_EXPIRATION_MINUTES * 60 * 1000,
    };

    this.saveState(updatedState);
  }

  /**
   * Update individual password fields as user types
   */
  updatePasswordField(
    field: 'password' | 'confirmPassword',
    value: string,
  ): void {
    const currentState = this.getForgotPasswordState();
    const updatedState: ForgotPasswordStateData = {
      ...currentState,
      [field]: value,
      formTouched: {
        ...currentState.formTouched,
        [field]: true,
      },
      expiresAt: Date.now() + this.DEFAULT_EXPIRATION_MINUTES * 60 * 1000,
    };

    this.saveState(updatedState);
  }

  /**
   * Update current step
   */
  setCurrentStep(step: 'SEND' | 'CONFIRM'): void {
    const currentState = this.getForgotPasswordState();
    const updatedState: ForgotPasswordStateData = {
      ...currentState,
      currentStep: step,
      expiresAt: Date.now() + this.DEFAULT_EXPIRATION_MINUTES * 60 * 1000,
    };

    this.saveState(updatedState);
  }

  /**
   * Update form interaction states
   */
  updateFormTouched(
    formTouched: Partial<ForgotPasswordStateData['formTouched']>,
  ): void {
    const currentState = this.getForgotPasswordState();
    const updatedState: ForgotPasswordStateData = {
      ...currentState,
      formTouched: {
        ...currentState.formTouched,
        ...formTouched,
      },
      expiresAt: Date.now() + this.DEFAULT_EXPIRATION_MINUTES * 60 * 1000,
    };

    this.saveState(updatedState);
  }

  /**
   * Get current forgot password state
   */
  getForgotPasswordState(): ForgotPasswordStateData {
    // Always read from localStorage to ensure we have the most current state
    try {
      const savedState = localStorage.getItem(this.FORGOT_PASSWORD_STATE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);

        // Check if state has expired
        if (parsedState.expiresAt && Date.now() > parsedState.expiresAt) {
          console.log(
            `[ForgotPasswordState ${this.serviceId}] State expired during read, clearing...`,
          );
          this.clearState();
          return {};
        }

        return parsedState;
      }
    } catch (error) {
      console.error(
        `[ForgotPasswordState ${this.serviceId}] Error reading state from localStorage`,
        error,
      );
    }

    // Fallback to BehaviorSubject value
    return this.forgotPasswordStateSource.value;
  }

  /**
   * Check if there's persisted forgot password state
   */
  hasPersistedState(): boolean {
    const state = this.getForgotPasswordState();
    return !!(state.email || state.currentStep);
  }

  /**
   * Get data prepared for 2FA flow
   */
  getDataFor2FA(): { email: string; password: string } | null {
    const state = this.getForgotPasswordState();

    if (state.email && state.password) {
      return {
        email: state.email,
        password: state.password,
      };
    }

    return null;
  }

  /**
   * Clear the forgot password state completely
   */
  clearState(): void {
    this.forgotPasswordStateSource.next({});
    localStorage.removeItem(this.FORGOT_PASSWORD_STATE_KEY);
  }

  /**
   * Clear state after successful password reset
   */
  clearStateAfterSuccess(): void {
    this.clearState();
  }

  /**
   * Clear state when user leaves the forgot password flow
   */
  clearStateOnRouteLeave(): void {
    this.clearState();
  }

  /**
   * Private method to save state to both BehaviorSubject and localStorage
   */
  private saveState(state: ForgotPasswordStateData): void {
    this.forgotPasswordStateSource.next(state);

    try {
      const serializedState = JSON.stringify(state);
      localStorage.setItem(this.FORGOT_PASSWORD_STATE_KEY, serializedState);
    } catch (error) {
      // Silently handle localStorage errors
    }
  }

  /**
   * Check if the current state is still valid (not expired)
   */
  isStateValid(): boolean {
    const state = this.getForgotPasswordState();
    if (!state.expiresAt) return false;

    return Date.now() < state.expiresAt;
  }

  /**
   * Get remaining time before expiration in minutes
   */
  getRemainingTimeMinutes(): number {
    const state = this.getForgotPasswordState();
    if (!state.expiresAt) return 0;

    const remainingMs = state.expiresAt - Date.now();
    return Math.max(0, Math.floor(remainingMs / (1000 * 60)));
  }
}
